import { OPCOES_FILTRO_DATA } from '@constants/Text'
import { minhaCarteira } from '@constants/URL'
import moment from 'moment'
import store from '@state/store'

const CUSTOM_DATE_FILTER = 'Específica'
export const UNTIL_LAST_MONTH_FILTER = 'Até mês anterior'

/**
 * Computa os parâmetros de data (begin_date e end_date) baseados no filtro selecionado
 * @param {Object} options - Opções para cálculo de datas
 * @param {String} options.filtroData - Filtro de data selecionado (ex: '12 Meses', '6 Meses', etc)
 * @param {String} options.dataInicio - Data de início personalizada (YYYY-MM-DD)
 * @param {String} options.dataFim - Data de fim personalizada (YYYY-MM-DD)
 * @param {Function} options.dateFactory - Factory de datas para injeção de dependência (usado em testes)
 * @returns {Object} Objeto com begin_date e end_date
 */
export const computeDateParams = ({
  filtroData,
  dataInicio,
  dataFim,
  dateFactory = moment,
} = {}) => {
  if (!filtroData || filtroData === OPCOES_FILTRO_DATA.INICIO) {
    return {}
  }

  if (filtroData === CUSTOM_DATE_FILTER) {
    return getCustomDateParams(dataInicio, dataFim, dateFactory)
  }

  return getPresetDateParams(filtroData, dateFactory)
}

const getCustomDateParams = (dataInicio, dataFim, dateFactory) => {
  const params = {}
  if (dataInicio) {
    params.begin_date = dateFactory(dataInicio, 'YYYY-MM-DD').format(
      'DD-MM-YYYY'
    )
  }
  if (dataFim) {
    params.end_date = dateFactory(dataFim, 'YYYY-MM-DD').format('DD-MM-YYYY')
  }
  return params
}

const getPresetDateParams = (filtroData, dateFactory) => {
  const params = {}
  if (filtroData === OPCOES_FILTRO_DATA.SEMANA) {
    params.begin_date = dateFactory().startOf('week').format('DD-MM-YYYY')
  } else if (filtroData === OPCOES_FILTRO_DATA.MES) {
    params.begin_date = dateFactory().startOf('month').format('DD-MM-YYYY')
  } else if (filtroData === OPCOES_FILTRO_DATA.SEMESTRE) {
    params.begin_date = dateFactory().subtract(6, 'month').format('DD-MM-YYYY')
  } else if (filtroData === OPCOES_FILTRO_DATA.YTD) {
    params.begin_date = dateFactory().startOf('year').format('DD-MM-YYYY')
  } else if (filtroData === OPCOES_FILTRO_DATA.ANO) {
    params.begin_date = dateFactory().subtract(12, 'month').format('DD-MM-YYYY')
  } else if (filtroData === UNTIL_LAST_MONTH_FILTER) {
    params.end_date = dateFactory()
      .subtract(1, 'month')
      .endOf('month')
      .format('DD-MM-YYYY')
  }
  return params
}

/**
 * Construtor de rota para coletar as estatísticas
 * @param {Object} options - Opções para construção da URL
 * @param {String} options.group - Fator de agrupamento
 * @param {Boolean} options.graph - Carregar ou não os dados dos gráficos
 * @param {String} options.filtroData - Filtro de data selecionado
 * @param {String} options.dataInicio - Data de início personalizada (YYYY-MM-DD)
 * @param {String} options.dataFim - Data de fim personalizada (YYYY-MM-DD)
 * @param {Number} options.clienteId - ID do cliente (opcional, pega do store se não fornecido)
 * @param {Object} options.store - Instância do Vuex store (default: store global)
 * @param {Function} options.dateFactory - Factory de datas para injeção de dependência (usado em testes)
 * @returns {String} URL construída para as estatísticas
 */
export function getUrlStatistics({
  group,
  graph,
  filtroData,
  dataInicio,
  dataFim,
  clienteId,
  store: storeParam = store,
  dateFactory = moment,
} = {}) {
  let url = ''

  if (group) {
    const idCliente = getClientId(storeParam, clienteId)
    const isClient = (storeParam.getters['user/currentUser'] || {}).IS_CLIENTE

    url = buildBaseUrl(isClient, idCliente)
    url += `?group_by=${group}&with_graph=${graph}`
  }

  const dateParams = computeDateParams({
    filtroData,
    dataInicio,
    dataFim,
    dateFactory,
  })

  url = appendDateParams(url, dateParams)
  url += '&group_assets_by=subclasse'

  return url
}

const getClientId = (storeParam, clienteId) => {
  const currentClient = storeParam.getters['user/currentClient'] || {}
  return clienteId || currentClient.id
}

const buildBaseUrl = (isClient, idCliente) => {
  if (isClient) {
    return minhaCarteira.cliente.estatisticas_benchmark
  }
  return minhaCarteira.estatisticas_benchmark + idCliente
}

const appendDateParams = (url, dateParams) => {
  if (dateParams.begin_date) {
    url += `&begin_date=${dateParams.begin_date}`
  }
  if (dateParams.end_date) {
    url += `&end_date=${dateParams.end_date}`
  }
  return url
}
